import { FilterDialogContent } from '@/components/FilterBar/FilterBar';
import { Skeleton } from '@/components/Skeleton';
import { GroupedTabs, Input, Text } from '@/design-system';
import { useGroupRunsStats, useRunStats } from '@/hooks/useSwr';
import { xCount } from '@/utils/stringUtils';

import {
  CUSTOM_TIME_RANGE_VALUE,
  SAMPLE_SIZE_MAX,
  SAMPLE_SIZE_MIN,
  TIME_RANGE_OPTIONS,
} from '../constants';
import { InsightsJobFormData } from '../types';
import { getStartAndEndTime } from '../utils/formatUtils';
import { ErrorText, FormSection } from './ConfigSectionComponents';

const CalendarInput = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) => (
  <input
    type="datetime-local"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="rounded-md border border-secondary bg-background p-2 text-xs focus:border-brand"
  />
);

export const SelectRunsSection = ({
  formData,
  updateFormData,
  errors,
  sessionId,
}: {
  formData: InsightsJobFormData;
  updateFormData: (updates: Partial<InsightsJobFormData>) => void;
  errors: Partial<Record<keyof InsightsJobFormData, string>>;
  sessionId: string;
}) => {
  const { start_time, end_time } = getStartAndEndTime({
    lastNHours: formData.timeRange.last_n_hours,
    startTime: formData.timeRange.start_time,
    endTime: formData.timeRange.end_time,
  });

  const { data: threadsStats, isLoading: isLoadingThreadsStats } =
    useGroupRunsStats(
      sessionId
        ? {
            session_id: sessionId,
            group_by: 'conversation',
            start_time,
            end_time,
            filter: formData.filter.filter,
          }
        : null
    );

  const { data: runStats, isLoading: isLoadingRunStats } = useRunStats({
    session: sessionId ? [sessionId] : [],
    start_time,
    end_time,
    filter: formData.filter.filter,
  });

  const isLoading = isLoadingRunStats || isLoadingThreadsStats;
  const hasThreads = threadsStats?.group_count ?? 0 > 0;

  return (
    <>
      <Text variant="h2" weight="medium">
        Select Runs
      </Text>
      <FormSection title="Job Name">
        <Input
          type="text"
          value={formData.name || ''}
          onChange={(value) => updateFormData({ name: value })}
          placeholder="Enter name here"
        />
        <ErrorText error={errors.name} />
      </FormSection>

      <FormSection
        title="Sample Size"
        description="Choose how many runs to include. For projects with threads, only the most recent run from each thread is included."
      >
        <Input
          type="number"
          value={formData.sample.toString()}
          onChange={(value) =>
            updateFormData({ sample: parseFloat(value) || 0 })
          }
          placeholder="Enter sample size (e.g., 200) or sampling rate (e.g., 0.1)"
          min="1"
          max="1000"
          step="1"
          className="max-w-[200px]"
        />
        <Text variant="xs" className="text-quaternary">
          {`Choose a number between ${SAMPLE_SIZE_MIN} and ${SAMPLE_SIZE_MAX}.`}
        </Text>
        <ErrorText error={errors.sample} />
      </FormSection>

      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap gap-3">
          <FormSection title="Time Range">
            <GroupedTabs
              value={formData.timeRange.last_n_hours ?? CUSTOM_TIME_RANGE_VALUE}
              onChange={(value: number) => {
                if (value === CUSTOM_TIME_RANGE_VALUE) {
                  updateFormData({
                    timeRange: {
                      last_n_hours: CUSTOM_TIME_RANGE_VALUE,
                    },
                  });
                } else {
                  updateFormData({
                    timeRange: {
                      last_n_hours: value,
                    },
                  });
                }
              }}
              options={TIME_RANGE_OPTIONS.map((option) => ({
                value: option.value,
                display: option.label,
              }))}
              className="whitespace-nowrap py-[3px]"
            />
          </FormSection>

          {formData.timeRange.last_n_hours === CUSTOM_TIME_RANGE_VALUE && (
            <div className="flex flex-row gap-3">
              <FormSection title="Start time">
                <CalendarInput
                  value={formData.timeRange.start_time || ''}
                  onChange={(value) =>
                    updateFormData({
                      timeRange: { ...formData.timeRange, start_time: value },
                    })
                  }
                />
              </FormSection>
              <FormSection title="End time">
                <CalendarInput
                  value={formData.timeRange.end_time || ''}
                  onChange={(value) =>
                    updateFormData({
                      timeRange: { ...formData.timeRange, end_time: value },
                    })
                  }
                />
              </FormSection>
            </div>
          )}
        </div>
        <ErrorText error={errors.timeRange} />
      </div>

      <FormSection
        title="Filters"
        description="Choose conditions to include only certain runs. For example, filter by tags, attributes, or metadata."
      >
        <div className="rounded-md border border-secondary px-2 pb-2 pt-2">
          <FilterDialogContent
            stats={runStats}
            value={formData.filter}
            onChange={(value) => updateFormData({ filter: value })}
            simpleFiltersOnly={true}
            showSaveFilters={false}
            setFilterBarOpen={() => {}}
            hideAIQuery={true}
            hideFilterTitle={true}
            updateEveryChange={true}
          />
        </div>
        {isLoading ? (
          <Skeleton className="my-0 w-[150px]" />
        ) : (
          (runStats || threadsStats) && (
            <div className="flex flex-row items-center gap-1">
              <Text variant="xs" className="text-quaternary">
                Matches
              </Text>
              <Text variant="xs" className="text-quaternary">
                {xCount(
                  hasThreads ? 'thread' : 'run',
                  hasThreads
                    ? threadsStats?.group_count || 0
                    : runStats?.run_count || 0
                )}
              </Text>
            </div>
          )
        )}
      </FormSection>
    </>
  );
};
